import React, { useState, useEffect } from 'react';
import MultiSelectDropdown from '../layout/MultiSelectDropdown1';
import PicklistApi from '../../api/PicklistApi';
import { normalizePicklistValuesArray } from '../reports/charts/chartPicklistColors';
import {
  resolveMultipicklistSelectedKeys,
  resolvePicklistSelectValue,
} from '../../utils/picklistValueUtils';

function getPicklistFieldConfigId(field) {
  if (!field) return null;
  const id = field.id ?? field.field_config_id ?? field.field_id;
  return id != null && String(id).trim() !== '' ? String(id) : null;
}

function getPicklistDataType(field) {
  return String(field?.data_type || field?.dataType || '').toLowerCase();
}

function isPicklistDataType(dataType) {
  return dataType === 'picklist' || dataType === 'multipicklist';
}

/** MultiSelectDropdown expects `{ key, value }` (value = display label). */
export function formatPicklistRowsForDropdown(rows) {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((item) => {
      const key = item?.value_key ?? item?.key ?? item?.value;
      const label =
        item?.value_label ?? item?.label ?? item?.value ?? item?.key ?? key;
      if (key == null || String(key).trim() === '') return null;
      return { key: String(key).trim(), value: String(label ?? key).trim() };
    })
    .filter(Boolean);
}

function getEmbeddedPicklistOptions(field) {
  const raw =
    normalizePicklistValuesArray(field?.picklist_values_data) ||
    normalizePicklistValuesArray(field?.picklist_values) ||
    normalizePicklistValuesArray(field?.picklistValues);
  return formatPicklistRowsForDropdown(raw);
}

/**
 * PicklistFieldSelector - Wrapper around MultiSelectDropdown for picklist fields
 * Handles loading picklist values and converting between value_key/value_label format
 */
const PicklistFieldSelector = ({
  field,
  value,
  onChange,
  disabled = false,
  isMultiSelect = false,
  /** When set (including []), skip API fetch and use these options (e.g. list view prefetch). */
  prefetchedPicklistOptions,
}) => {
  const [picklistValues, setPicklistValues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const picklistApi = new PicklistApi();

  const fieldConfigId = getPicklistFieldConfigId(field);
  const dataType = getPicklistDataType(field);

  // Load picklist values when field changes (or use list-view prefetch when provided)
  useEffect(() => {
    if (!fieldConfigId || !isPicklistDataType(dataType)) {
      setPicklistValues([]);
      setLoading(false);
      return;
    }
    if (prefetchedPicklistOptions !== undefined) {
      setPicklistValues(
        formatPicklistRowsForDropdown(
          Array.isArray(prefetchedPicklistOptions) ? prefetchedPicklistOptions : []
        )
      );
      setLoading(false);
      return;
    }
    const embedded = getEmbeddedPicklistOptions(field);
    if (embedded.length > 0) {
      setPicklistValues(embedded);
      setLoading(false);
      return;
    }
    loadPicklistValues(fieldConfigId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when field id/type or embedded data changes
  }, [fieldConfigId, dataType, prefetchedPicklistOptions, field?.picklist_values_data, field?.picklist_values]);

  // Parse value prop to set selected keys (case-insensitive match to option keys)
  useEffect(() => {
    if (!value) {
      setSelectedKeys([]);
      return;
    }
    if (isMultiSelect) {
      setSelectedKeys(resolveMultipicklistSelectedKeys(picklistValues, value));
    } else {
      const resolved = resolvePicklistSelectValue(picklistValues, value);
      setSelectedKeys(resolved ? [resolved] : [value]);
    }
  }, [value, isMultiSelect, picklistValues]);

  const loadPicklistValues = async (fieldId) => {
    if (!fieldId) return;

    setLoading(true);
    try {
      const response = await picklistApi.getActivePicklistValues(fieldId);
      if (response.data?.success) {
        const values = response.data.data || [];
        setPicklistValues(formatPicklistRowsForDropdown(values));
      } else {
        setPicklistValues([]);
      }
    } catch (error) {
      console.error('Error loading picklist values:', error);
      setPicklistValues([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (selectedKeysArray) => {
    let finalKeys = selectedKeysArray;

    // For single select, only keep one selected item at a time
    if (!isMultiSelect) {
      // If multiple items are selected, only keep the last one
      finalKeys = selectedKeysArray.length > 1
        ? [selectedKeysArray[selectedKeysArray.length - 1]]
        : selectedKeysArray;
    }

    setSelectedKeys(finalKeys);

    if (isMultiSelect) {
      // Join with semicolon for multi-select
      const newValue = finalKeys.join(';');
      onChange(newValue);
    } else {
      // Single value for single select
      const newValue = finalKeys.length > 0 ? finalKeys[0] : '';
      onChange(newValue);
    }
  };

  // Don't render if not a picklist field
  if (!field || !isPicklistDataType(dataType)) {
    return null;
  }

  return (
    <MultiSelectDropdown
      selectedValues={selectedKeys}
      picklistValues={picklistValues}
      onChange={handleChange}
      disabled={disabled || loading}
      placeholder={loading ? 'Loading...' : (isMultiSelect ? 'Select values...' : 'Select value...')}
      isRequired={false}
      forceOpen={false}
    />
  );
};

export default PicklistFieldSelector;
